import React, { useState, useEffect, useRef } from 'react';
import { Card, Row, Col, Input, Button, Form, FormGroup, Label, Table, Alert, CardBody } from 'reactstrap';
import { FaArrowLeft, FaEdit } from 'react-icons/fa';
import { faCheckCircle, faPaperPlane, faTruck, faFire, faClipboardList, faClock } from '@fortawesome/free-solid-svg-icons';
import { useNavigate, useLocation } from 'react-router-dom';
import RejectQuotationPopup from './RejectQuotationPopup';
import DashboardService from "../Dashboard/dashboard.service";
import AuthService from "../Authentication/auth.service";
import Alerts from "../../components/Alerts/alerts";
import { useTranslation } from 'react-i18next';
import LoadingOverlay from "react-loading-overlay-ts";
import DNALoader from "../../components/Loaders/DnaLoader";
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import DiscountCouponService from "../DiscountCoupon/Discountcoupons.service";
import QuotationModal from './QuotationModal';
import { toWords } from 'number-to-words'
import {
  faSpinner,
  faBox, faBan, faQuestion
} from '@fortawesome/free-solid-svg-icons';
import services from "./services";
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { FaDownload } from 'react-icons/fa';
import { io } from 'socket.io-client';

const QuotationsDetails = () => {
  const [rfqDetails, setRfqDetails] = useState(null);
  const [orderCreditNoteDetails, setOrderCreditNoteDetails] = useState(null);
  const [orderTrackingDetails, setOrderTrackingDetails] = useState(null);
  const [orderInvoiceDetails, setInvoiceDetails] = useState(null);
  const [orderPaymentDetails, setPaymentDetails] = useState(null);
  const [activeTab, setActiveTab] = useState('details');
  const [loading, setLoading] = useState(true);
  const [isPopupVisible, setPopupVisible] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  // Support id from both route params and location.state for /quotation_details/:id and /quotation_details
  const id = (location.state && location.state.id) || (location.pathname.split('/').pop() !== 'quotation_details' ? location.pathname.split('/').pop() : null);
  const chatContainerRef = useRef(null);
  const { t } = useTranslation();
  const sessionID = localStorage.getItem("session-id");
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isEditMode, setIsEditMode] = useState(false);
  const [quotationDetails, setQuotationDetails] = useState(null);
  const [isQuotationModalOpen, setIsQuotationModalOpen] = useState(false);
  const [outOfStockItems, setOutOfStockItems] = useState([]);
  const [discount, setDiscount] = useState(0);
  const [paymentTerms, setPaymentTerms] = useState('net-30');
  const [createNewOrderForOOS, setCreateNewOrderForOOS] = useState(true);
  // Helper functions
  const [logo, setLogo] = useState(localStorage.getItem("system_logo"));
  const [returnReason, setReturnReason] = useState('');
  // Notification helpers and refs
  const canNotify = () => typeof window !== 'undefined' && 'Notification' in window;
  const requestNotifyPermission = async () => {
    try {
      if (!canNotify()) return false;
      if (Notification.permission === 'granted') return true;
      if (Notification.permission !== 'denied') {
        const p = await Notification.requestPermission();
        return p === 'granted';
      }
      return false;
    } catch (_) { return false; }
  };
  const pushNotify = async (title, body) => {
    try {
      const ok = await requestNotifyPermission();
      if (!ok) return;
      new Notification(title, { body, icon: logo || '/favicon.ico' });
    } catch (_) {}
  };
  const lastMessageCountRef = useRef(0);
  const socketRef = useRef(null);

  const formatStatus = (status) => {
    if (!status) return '';
    return status
      ?.toLowerCase()
      ?.split('_')
      ?.map(word => word.charAt(0).toUpperCase() + word.slice(1))
      ?.join(' ');
  };


  const calculateTotals = (items) => {
    const subtotal = items?.reduce((total, item) => {
      const availableQuantity = item?.selectedBatches?.reduce((sum, batch) => sum + (batch.quantity || item.quantity), 0) || item.quantity;
      return total + (availableQuantity * item?.price || 0);
    }, 0);

    const total_discount = items?.reduce((discount, item) => {
      // Calculate discount for each batch separately and sum them up
      const batchDiscount = item?.selectedBatches?.reduce((batchSum, batch) => {
        const batchQuantity = batch.quantity || item.quantity || 0;
        const batchDiscountAmount = ((item?.discount || 0) / 100) * (item?.price || 0) * batchQuantity;
        return batchSum + batchDiscountAmount;
      }, 0) || 0;
      
      return discount + batchDiscount;
    }, 0);

    const totalAmount = subtotal - total_discount;
    return { subtotal, discountPercentage: total_discount, totalAmount };
  };

  const renderItemRows = (items) => {
    const groupedItems = items?.reduce((acc, item) => {
      const category = item?.ItemGroup || item?.categoryDetails?.Name || '';
      if (!acc[category]) acc[category] = [];
      acc[category].push(item);
      return acc;
    }, {});

    return Object.entries(groupedItems).flatMap(([category, categoryItems]) =>
      categoryItems.map((item, itemIndex) => {
        const batches = item?.selectedBatches || [];
        const renderStack = (nodes) => (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>{nodes}</div>
        );

        return (
          <tr key={`${category}-${itemIndex}`}>
            <td>{itemIndex + 1}</td>
            <td>
              <div className="d-flex align-items-center justify-content-center flex-column">
                <strong>{item?.itemDetails?.item_name}</strong>
              </div>
            </td>
            <td>{item?.quantity}</td>
            <td>{item?.itemDetails?.BaseUOM || ''}</td>
            <td>{item?.price ? `RM ${parseFloat(item?.price).toFixed(2)}` : ''}</td>
            <td>
              {item?.discount ? (
                renderStack(
                  batches.map((b, idx) => (
                    <span key={idx}>{`RM ${(((item.discount || 0) / 100) * (item.price || 0) * (b.quantity || 0)).toFixed(2)} (${item.discount}%)`}</span>
                  ))
                )
              ) : (
                '-'
              )}
            </td>
            <td>
              {renderStack(batches.map((b, idx) => <span key={idx}>{b.batch_number || '-'}</span>))}
            </td>
            <td>
              {renderStack(batches.map((b, idx) => <span key={idx}>{b.quantity || '-'}</span>))}
            </td>
            <td>
              {renderStack(batches.map((b, idx) => <span key={idx}>{b?.expiry ? moment(b?.expiry).format('DD-MM-YYYY') : ''}</span>))}
            </td>
            <td>
              {renderStack(
                batches.map((b, idx) => (
                  <span key={idx}>{`RM ${(
                    (parseFloat(item?.price || 0) * parseFloat(b?.quantity || 0)) -
                    (((item?.discount || 0) / 100) * (item?.price || 0) * (b?.quantity || 0))
                  ).toFixed(2)}`}</span>
                ))
              )}
            </td>
          </tr>
        );
      })
    );
  };

  const formatCurrency = (value) => {
    const parsedValue = parseFloat(value);
    return !isNaN(parsedValue) ? parsedValue.toFixed(2) : '0.00';
  };

  const formatPaymentTerm = (paymentTerm) => {
    //iph todo: 47
    //console.log("paymentTerm", paymentTerm);
    
    if (!paymentTerm) return '';
    
    switch (paymentTerm.toLowerCase()) {
      case 'net-30':
        return 'Net 30 days';
      case 'net-60':
        return 'Net 60 days';
      case 'net-90':
        return 'Net 90 days';
      case 'advance':
        return 'C.O.D.';
      default:
        return paymentTerm;
    }
  };

  useEffect(() => {
    fetchRfqDetails();
  }, [id]);

  // Request notification permission once
  useEffect(() => {
    requestNotifyPermission();
  }, []);

  // Real-time updates: no polling; refresh on tab visibility and SW events only
  useEffect(() => {
    const onVisibility = () => {
      if (!document.hidden) {
        fetchMessages();
      }
    };
    const onSwMessage = (event) => {
      try {
        if (event?.data?.type === 'FCM_BACKGROUND_MESSAGE') {
          fetchMessages();
        }
      } catch (_) {}
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('message', onSwMessage);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('message', onSwMessage);
    };
  }, [id, sessionID]);

  // Socket.io realtime subscription
  useEffect(() => {
    try {
      const s = io('/', { path: '/socket.io', transports: ['websocket'] });
      socketRef.current = s;
      if (id) s.emit('join_room', { orderId: id });
      s.on('order_message', (msg) => {
        setMessages((prev) => [...(prev || []), msg]);
        lastMessageCountRef.current = (lastMessageCountRef.current || 0) + 1;
        if (msg?.sender_type === 'doctor') {
          pushNotify('New message from customer', msg.message || 'You have a new message');
        }
      });
    } catch (_) {}
    return () => {
      try { socketRef.current?.disconnect(); } catch (_) {}
    };
  }, [id]);

  const fetchRfqDetails = async () => {
    setLoading(true);
    try {
      var payload = {
        functionName: "GetRFQDetails",
        postData: {
          _id: id,
        }
      }

      const response = await DashboardService.GetRFQDetails(payload);
      if (response.data.status) {
        //iph todo: 47
        //console.log("response.data.data", response.data.data)
        setRfqDetails(response?.data?.data?.[0]);
        setReturnReason(response?.data?.data?.[0]?.return_reason || '')
        setOrderTrackingDetails(response?.data?.orderTrackingDetails);
        setOrderCreditNoteDetails(response?.data?.orderCreditNoteDetails);
        setInvoiceDetails(response?.data?.orderInvoiceDetails);
        setPaymentDetails(response?.data?.orderPaymentDetails);
        setDiscount(response?.data?.data?.[0]?.discount || 0);
      } else {
        Alerts.swalErrorAlert(response.data.message || t("Failed to fetch Order details"));
      }
    } catch (error) {
      console.error("Error fetching Order details:", error);
      Alerts.swalErrorAlert(t("An error occurred while fetching Order details"));
    } finally {
      setLoading(false);
    }
  };

  const openPopup = () => setPopupVisible(true);
  const closePopup = () => setPopupVisible(false);

  const handleEditQuotation = () => {
    setIsEditMode(true);
    IssueQuotation();
  };

  const fetchMessages = async () => {
    try {
      const response = await DiscountCouponService.GetOrderMessages(sessionID, id, 'admin');
      if (response.data.message === "Unauthorized") {
        AuthService.logout();
        navigate("/login");
        window.location.reload();
      } else if (response.data) {
        const newMsgs = response.data.messages || [];
        const prevCount = lastMessageCountRef.current || 0;
        setMessages(newMsgs);
        if (prevCount && newMsgs.length > prevCount) {
          const lastMsg = newMsgs[newMsgs.length - 1];
          if (lastMsg?.sender_type === 'doctor') {
            pushNotify('New message from customer', lastMsg.message || 'You have a new message');
          }
        }
        lastMessageCountRef.current = newMsgs.length;
      }
    } catch (error) {
      console.error("Error fetching messages:", error);
    }
  };

  const sendMessage = async () => {
    if (newMessage.trim() === '') return;

    try {
      await DiscountCouponService.SendOrderMessage(sessionID, id, newMessage, 'admin');
      setNewMessage('');
      fetchMessages(); // Refresh messages after sending
    } catch (error) {
      console.error("Error sending message:", error);
    }
  };
  const IssueQuotation = async () => {
    try {
      setLoading(true);
      var payload = {
        functionName: "GetQuotationDetails",
        postData: {
          _id: rfqDetails?._id,
        }
      }

      const response = await services.GetQuotationDetails(payload, sessionID);
      if (response.data.status) {
        //iph todo: 47
        //console.log("response", response)
        setQuotationDetails(response.data.data);
        setOutOfStockItems(response?.data?.data?.outOfStockItems || []);
        // Default checked as requested, only relevant when there are OOS items
        setCreateNewOrderForOOS(true);
        setIsQuotationModalOpen(true);
      } else {
        Alerts.swalErrorAlert(response.data.message || t("Failed to get quotation details"));
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error("Error getting quotation details:", error);
      Alerts.swalErrorAlert(t("An error occurred while getting quotation details"));
    }
  };

  const handlePriceChange = (index, newPrice) => {
    setQuotationDetails(prevDetails => {
      const updatedItems = [...prevDetails.items];
      updatedItems[index] = { ...updatedItems[index], price: newPrice };
      return {
        ...prevDetails,
        items: updatedItems
      };
    });
  };

  const handleDiscountChange = (index, newDiscount) => {
    setQuotationDetails(prevDetails => {
      const updatedItems = [...prevDetails.items];
      updatedItems[index] = { ...updatedItems[index], discount: newDiscount || 0 };
      return {
        ...prevDetails,
        items: updatedItems
      };
    });
  };

  // const handleDiscountChange = (newDiscount) => {
  //   setDiscount(newDiscount);
  // }

  const handlePaymentTermsChange = (newTerms) => {
    setPaymentTerms(newTerms);
  };

  const handleBatchChange = (itemIndex, batchIndex, newBatchId) => {
    setQuotationDetails(prevDetails => {
      const newItems = [...prevDetails.items];
      const item = newItems[itemIndex];
      
      // Check if this batch is already selected in other batch rows
      const isAlreadySelected = item.selectedBatches
        .some((batch, idx) => idx !== batchIndex && batch.batchId === newBatchId);
      
      if (isAlreadySelected) {
        // Don't allow selecting the same batch twice
        return prevDetails;
      }
      
      const newBatch = item.availableBatches.find(batch => batch.batchId === newBatchId);

      if (newBatch) {
        item.selectedBatches[batchIndex] = {
          batchId: newBatch.batchId,
          batch_number: newBatch.batch_number,
          quantity: Math.min(newBatch.quantity, item.selectedBatches[batchIndex]?.quantity || 0),
          expiry: newBatch.expiry
        };
      } else {
        // Clear the batch if no valid selection
        item.selectedBatches[batchIndex] = {
          batchId: '',
          batch_number: '',
          quantity: 0,
          expiry: null
        };
      }

      return { ...prevDetails, items: newItems };
    });
  };

  const handleQuantityChange = (itemIndex, batchIndex, newQuantity) => {
    setQuotationDetails(prevDetails => {
      const newItems = [...prevDetails.items];
      const item = newItems[itemIndex];
      const batch = item.selectedBatches[batchIndex];
      const availableBatch = item.availableBatches.find(ab => ab.batchId === batch.batchId);

      if (availableBatch) {
        const parsedQuantity = parseInt(newQuantity, 10) || 0;
        
        // Calculate how much of this batch is already used by other batch selections
        const usedQuantity = item.selectedBatches
          .filter((b, idx) => idx !== batchIndex && b.batchId === batch.batchId)
          .reduce((sum, b) => sum + (parseInt(b.quantity) || 0), 0);
        
        const maxAvailable = Math.max(0, availableBatch.quantity - usedQuantity);
        batch.quantity = Math.min(Math.max(0, parsedQuantity), maxAvailable);
      }

      return { ...prevDetails, items: newItems };
    });
  };

  const handleAddBatch = (itemIndex) => {
    setQuotationDetails(prevDetails => {
      const newItems = [...prevDetails.items];
      const item = newItems[itemIndex];
      
      // Add a new empty batch
      const newBatch = {
        batchId: '',
        batch_number: '',
        quantity: 0,
        expiry: null
      };
      
      item.selectedBatches = [...(item.selectedBatches || []), newBatch];
      
      return { ...prevDetails, items: newItems };
    });
  };

  const handleRemoveBatch = (itemIndex, batchIndex) => {
    setQuotationDetails(prevDetails => {
      const newItems = [...prevDetails.items];
      const item = newItems[itemIndex];
      
      // Only allow removal if there's more than one batch
      if (item.selectedBatches && item.selectedBatches.length > 1) {
        item.selectedBatches.splice(batchIndex, 1);
      }
      
      return { ...prevDetails, items: newItems };
    });
  };

  const saveQuotation = async () => {
    try {
      const quotationData = {
        ...quotationDetails,
        discount,
        paymentTerms,
        rfq_id: rfqDetails?._id,
        isEdit: isEditMode
      };
      const response = await services.SaveQuotation(quotationData, sessionID);
      if (response.data.status) {
        Alerts.swalSuccessAlert(t(isEditMode ? "Quotation updated successfully" : "Quotation saved successfully"));
        setIsQuotationModalOpen(false);
        setIsEditMode(false);
        fetchRfqDetails();
      } else {
        Alerts.swalErrorAlert(response.data.message || t("Failed to save quotation"));
      }
    } catch (error) {
      console.error("Error saving quotation:", error);
      Alerts.swalErrorAlert(t("An error occurred while saving the quotation"));
    }
  };

  const finalIssueQuotation = async () => {
    try {
      setLoading(true);
      
      // Validate that all items have valid batch quantities
      if (quotationDetails && quotationDetails.items) {
        const invalidItems = quotationDetails.items.filter(item => {
          const totalSelected = item.selectedBatches?.reduce((sum, batch) => sum + (parseInt(batch.quantity) || 0), 0) || 0;
          const totalAvailable = item.availableBatches?.reduce((sum, batch) => sum + batch.quantity, 0) || 0;
          
          // Allow partial fulfillment if total available is less than requested
          const maxPossible = Math.min(item.quantity, totalAvailable);
          return totalSelected > maxPossible || totalSelected < 0;
        });
        
        if (invalidItems.length > 0) {
          Alerts.swalErrorAlert(t("Please ensure that the selected quantities are valid and do not exceed available stock."));
          setLoading(false);
          return;
        }
      }
      
      let data;
      if (quotationDetails === null) {
        // Round all discount values in rfqDetails to 2 decimal places before sending to server
        data = {
          ...rfqDetails,
          items: rfqDetails?.items?.map(item => ({
            ...item,
            discount: item.discount ? parseFloat(parseFloat(item.discount).toFixed(2)) : 0
          })) || []
        };
      } else {
        // Round all discount values to 2 decimal places before sending to server
        data = {
          ...quotationDetails,
          items: quotationDetails.items?.map(item => ({
            ...item,
            discount: item.discount ? parseFloat(parseFloat(item.discount).toFixed(2)) : 0
          })) || []
        };
      }
      //iph todo: 47
      //console.log("data", data)
      var payload = {
        ...data,
        discount: discount ? parseFloat(parseFloat(discount).toFixed(2)) : 0,
        functionName: "finalIssueQuotation",
        paymentTerms,
        _id: rfqDetails?._id,
        // Create a new order for out-of-stock items if checkbox is checked
        create_new_order_for_oos: createNewOrderForOOS,
        // Request server to mark such items/orders as Back Order
        back_order_status: 'Back Order'
      }

      const response = await services.FinalIssueQuotation(payload, sessionID);
      if (response.data.status) {
        Alerts.swalSuccessAlert(t("Quotation issued successfully"));
        setIsQuotationModalOpen(false);
        setLoading(false);
        fetchRfqDetails();
      } else {
        Alerts.swalErrorAlert(response.data.message || t("Failed to Verify Invoice"));
        setLoading(false);
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error("Error issuing quotation:", error);
      Alerts.swalErrorAlert(t("An error occurred while issuing the quotation"));
    }
  };

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'pending':
        return '#FFA500';
      case 'confirmed':
        return '#4169E1';
      case 'processing':
        return '#9370DB';
      case 'in_transit':
        return '#1E90FF';
      case 'delivered':
        return '#32CD32';
      case 'cancelled':
        return '#DC143C';
      case 'order paid':
        return '#DC143C';
      case 'order received':
        return '#DC143C';
      default:
        return '#808080';
    }
  };

  const getIconForStatus = (status) => {
    switch (status.toLowerCase()) {
      case 'pending':
        return faClock;
      case 'confirmed':
        return faCheckCircle;
      case 'processing':
        return faSpinner;
      case 'in_transit':
        return faTruck;
      case 'delivered':
        return faBox;
      case 'cancelled':
        return faBan;
      case 'order paid':
        return faFire;
      case 'order received':
        return faClipboardList;
      default:
        return faQuestion;
    }
  };

  const renderOrderDetails = () => (
    <div className="item-list mb-3">
      <div className="container mt-4">
        {isPopupVisible && <RejectQuotationPopup quotationId={id} rfq_id={rfqDetails?.rfq_id} onClose={closePopup} fetchRfqDetails={fetchRfqDetails} />}
        <LoadingOverlay active={loading ? true : false} spinner={<DNALoader />} >
          <Card className="rfq-card">
            <div className="p-4">
              <Form>
                <Row className="mb-4">
                  <Col md={6}>
                    <FormGroup>
                      <Label for="rfq_date" className="text-muted mb-2">{t("Order Date")}</Label>
                      <Input type="text" id="rfq_date" className="form-select" readOnly value={new Date(rfqDetails?.datetime_added).toLocaleDateString()} />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label for="status" className="text-muted mb-2">{t("Status")}</Label>
                      <Input type="text" id="status" className="form-select" readOnly value={rfqDetails?.status} />
                    </FormGroup>
                  </Col>
                  {rfqDetails?.status === "rejected" &&
                    <Col md={12}>
                      <FormGroup>
                        <Label for="reason" className="text-muted mb-2">{t("Rejection Reason")}</Label>
                        <Input type="textarea" id="reason" readOnly value={rfqDetails?.reason} />
                      </FormGroup>
                    </Col>
                  }
                </Row>

                <h5 className="mb-3 fw-bold">{t("List of Item(s)")}</h5>
                <div className="item-list mb-3">
                  <Table responsive>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>{t("Item(s) - Brand")}</th>
                        <th>{t("Requested Qty")}</th>
                        <th>{t("UOM")}</th>
                        <th>{t("Unit Price")}</th>
                        <th>{t("Discount")}</th>
                        <th>{t("Batches")}</th>
                        <th>{t("Batch Quantity")}</th>
                        <th>{t("Expiry Date")}</th>
                        <th>{t("Total Price")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {renderItemRows(rfqDetails?.items || [])}
                    </tbody>
                  </Table>
                </div>

                {rfqDetails?.outOfStockItems?.length > 0 && (
                  <Alert color="warning">
                    The following items are out of stock or have insufficient quantity:
                    <ul>
                      {rfqDetails?.outOfStockItems?.map((item, index) => (
                        <li key={index}>
                          {item.item_name}: {item.extra_quantity} units out of stock
                        </li>
                      ))}
                    </ul>
                  </Alert>
                )}

                {rfqDetails?.status !== "Review" ?
                  <div className="totals text-end">
                    {(() => {
                      const { subtotal, discountPercentage, totalAmount } = calculateTotals(rfqDetails?.items || []);
                      return (
                        <>
                          {discountPercentage > 0 ?
                            <>
                              <div className="totals-row">
                                <span>{t("Subtotal")}:</span>
                                <span className="ms-3">RM {parseFloat(subtotal).toFixed(2)}</span>
                              </div>
                              <div className="totals-row">
                                <span>{t(`Discount`)}:</span>
                                <span className="ms-3">{parseFloat(discountPercentage).toFixed(2) || 0}</span>
                              </div>
                              <div className="totals-row total-amount fw-bold">
                                <span>{t("Total Amount")}:</span>
                                <span className="ms-3">RM {parseFloat(totalAmount).toFixed(2)}</span>
                              </div>
                            </>
                            :
                            <>
                              <div className="totals-row total-amount fw-bold">
                                <span>{t("Total Amount")}:</span>
                                <span className="ms-3">RM {parseFloat(totalAmount).toFixed(2)}</span>
                              </div>
                            </>
                          }
                        </>
                      );
                    })()}
                  </div>
                  : ''}
              </Form>
            </div>
          </Card>
        </LoadingOverlay>
      </div>
    </div>
  );

  const renderInvoiceContent = () => {
    if (!orderInvoiceDetails) return null;

    const invoice = orderInvoiceDetails;
    //iph todo: 47
    //console.log("invoice invoice", invoice)

    // Ensure `invoice.item` is defined and has data
    const items = invoice.item || [];
    if (items.length === 0) return renderNPDetails(); // Default case
    //iph todo: 47
    //console.log("items items", items)

    const allItemsArePoison = items.every(item => item.ItemGroup === 'POISON');
    const allItemsAreNP = items.every(item => item.ItemGroup === 'NP');
    //iph todo: 47
    //console.log("allItemsArePoison", allItemsArePoison)

    if (allItemsArePoison) {
      return renderPOISONDetails();
    }

    // If all items are NP, render NP details, otherwise default to NP
    return renderNPDetails();
  };


  const renderNPDetails = () => {
    // Sample data structure
    const invoice = orderInvoiceDetails;
    if (!invoice) return null;

    // const allItemsArePoison = items.every(item => item.ItemGroup === 'POISON');
    // const allItemsAreNP = items.every(item => item.ItemGroup === 'NP');

    const groupedItems = invoice.item?.reduce((acc, item) => {
      const group = item.ItemGroup || '';
      if (!acc[group]) {
        acc[group] = [];
      }
      acc[group].push(item);
      return acc;
    }, {});

    //iph todo: 47
    //console.log("groupedItems", groupedItems)

    const styles = {
      page: {
        fontFamily: 'Arial, sans-serif',
        fontSize: '15px',
        lineHeight: '1.4',
        color: '#000',
        backgroundColor: '#fff',
        width: '300mm',
        minHeight: '297mm',
        margin: '20px auto',
        paddingBottom: '15mm',
        paddingTop: '1mm',
        paddingLeft: '15mm',
        paddingRight: '15mm',
        boxSizing: 'border-box',
        position: 'relative',
      },
      card: {
        width: '300mm',
        minHeight: '297mm',
        border: 'none',
        boxShadow: 'none'
      },
      header: {
        borderBottom: "1px solid #000",
        paddingBottom: "10px",
        marginBottom: "10px",
      },
      headerContent: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
      },
      logo: {
        width: '150px',
        height: 'auto'
      },
      companyInfo: {
        fontSize: "18px",
        lineHeight: "1.3",
        textAlign: "center",
      },
      // companyInfo: {
      //   textAlign: 'right'
      // },
      // companyName: {
      //   fontSize: '16px',
      //   fontWeight: 'bold',
      //   marginBottom: '5px'
      // },
      // title: {
      //   fontSize: '16px',
      //   fontWeight: 'bold',
      //   marginBottom: '20px'
      // },

      companyName: {
        fontSize: "25px",
        fontWeight: "bold",
        marginBottom: "3px",
      },
      rfq: {
        fontSize: "15px",
        fontWeight: 'normal',
        marginBottom: "3px",
      },
      title: {
        fontSize: "20px",
        fontWeight: "bold",
        marginBottom: "15px",
        textAlign: "center",
      },
      table: {
        width: '100%',
        borderCollapse: 'collapse',
        marginBottom: '20px'
      },
      th: {
        borderTop: '1px solid #000',
        borderBottom: '1px solid #000',
        padding: '8px',
        textAlign: 'left'
      },
      td: {
        padding: '8px',
        borderBottom: '1px solid #ddd'
      },
      categoryHeader: {
        backgroundColor: '#f5f5f5',
        padding: '8px',
        fontWeight: 'bold'
      },
      footer: {
      },
      totalSection: {
        display: 'flex',
        justifyContent: 'space-between',
        marginBottom: '20px'
      },

      total: {
        marginTop: '40px',
        paddingTop: '20px',
      },

      totalAmountSection: {
        marginBottom: '20px',
      },

      totalRowCombined: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderTop: '1px solid #000',
        borderBottom: '1px solid #000',
        padding: '6px 0',
        fontWeight: 'bold',
        fontSize: '14px',
      },

      totalWords: {
        width: '75%',
        textTransform: 'uppercase',
      },

      totalLabelWithBox: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
      },

      totalLabel: {
        fontWeight: 'bold',
      },

      totalValueBox: {
        border: '1px solid #000',
        padding: '4px 12px',
        fontWeight: 'bold',
      },



      notes: {
        marginBottom: '0px'
      },
      signatures: {
        display: 'flex',
        justifyContent: 'space-between',
        marginTop: '60px'
      },
      signatureLine: {
        borderTop: '1px solid #000',
        width: '200px',
        marginTop: '40px'
      }
    };

    const renderHeader = () => (
      <div style={styles.header}>
        <div style={styles.headerContent}>
          <div style={styles.companyInfo}>
            <div style={{ ...styles.companyName, display: 'inline' }}>IPH Pharmaceuticals Sdn.Bhd. <div style={{ ...styles.rfq, display: 'inline' }}>(1129802-A)</div></div>
            <div>323, 1st Floor, Jalan Pudu, 55100 Kuala Lumpur, Wilayah Persekutuan , Malaysia</div>
            <div>Tel: 010-8726948</div>
            <div>TIN : C23874076050</div>
          </div>
        </div>
      </div>
    );

    const renderClientInfo = (pageNumber) => (
      <>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
          <div style={{ maxWidth: '48%' }}>
            <tr>
              <td style={{ whiteSpace: 'nowrap' }}>Debtor Code :</td>
              <td style={{ whiteSpace: 'nowrap' }}>{rfqDetails?.userDetails?.[0]?.AccNo}</td>
            </tr>
            <div style={{ fontWeight: 'bold', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {rfqDetails?.userDetails?.[0]?.clinic_name}
            </div>
            <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {rfqDetails?.userDetails?.[0]?.name}
            </div>
            <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {rfqDetails?.userDetails?.[0]?.Address}
            </div>
          </div>
          <div style={{ maxWidth: '48%' }}>
            <table>
              <tbody>
                <tr>
                  <td style={{ whiteSpace: 'nowrap' }}>Invoice / Poison Order No:</td>
                  <td style={{ whiteSpace: 'nowrap' }}>{invoice.invoice_number}</td>
                </tr>
                <tr>
                  <td style={{ whiteSpace: 'nowrap' }}>Invoice Date:</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {moment(invoice.datetime_added).format('DD/MM/YYYY')}
                  </td>
                </tr>
                <tr>
                  <td style={{ whiteSpace: 'nowrap' }}>Order Number:</td>
                  <td style={{ whiteSpace: 'nowrap' }}>{invoice?.order_number || ''}</td>
                </tr>
                <tr>
                  <td style={{ whiteSpace: 'nowrap' }}>Order Date:</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {moment(invoice.datetime_added).format('DD/MM/YYYY') || ''}
                  </td>
                </tr>
                <tr>
                  <td style={{ whiteSpace: 'nowrap' }}>Credit Term:</td>
                  <td style={{ whiteSpace: 'nowrap' }}>{formatPaymentTerm(orderInvoiceDetails?.paymentTerms || rfqDetails?.paymentTerms)}</td>
                </tr>
                {/* <tr>
                   <td style={{ whiteSpace: 'nowrap' }}>Page:</td>
                   <td style={{ whiteSpace: 'nowrap' }}>{pageNumber} of 2</td>
                 </tr> */}
              </tbody>
            </table>
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
          <div style={{ maxWidth: '48%' }}>
            <tr>
              <td style={{ whiteSpace: 'nowrap' }}>Tel :</td>
              <td style={{ whiteSpace: 'nowrap' }}>{rfqDetails?.userDetails?.[0]?.phone}</td>
            </tr>
          </div>
          <div style={{ maxWidth: '48%' }}>
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
          <div style={{ maxWidth: '60%' }}>
            <tr>
              <div>
                <div style={{ whiteSpace: 'nowrap' }}>Reason for use:</div>
                <div style={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>{rfqDetails?.reason_of_use}</div>
              </div>
            </tr>
          </div>
          <div style={{ maxWidth: '40%' }}>
          </div>
        </div>
      </>
    );

    const renderItemsTable = (items, showTotal = false) => {
      const headerThStyle = { ...styles.th };
      const descriptionTdStyle = { maxWidth: '55%', whiteSpace: 'normal', wordBreak: 'break-word' };
      return (
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={headerThStyle}>#</th>
              <th style={headerThStyle}>Description</th>
              <th style={headerThStyle}>Qty</th>
              <th style={headerThStyle}>Batch No</th>
              <th style={headerThStyle}>Expiry</th>
              <th style={headerThStyle}>U/Price (RM)</th>
              <th style={headerThStyle}>Discount (RM)</th>
              <th style={headerThStyle}>Amount (RM)</th>
            </tr>
          </thead>
          <tbody>
            <>
              {Object.entries(groupedItems).map(([category, items], categoryIndex) => (
                <React.Fragment key={categoryIndex}>
                  <tr>
                    <td colSpan="8" style={styles.categoryHeader}>{(category || 'NP').toString().toUpperCase()}</td>
                  </tr>
                  {items.map((item, itemIndex) => {
                    const batches = item.selectedBatches || [];
                    const isLastBatch = (idx) => idx === (batches.length - 1);
                    return batches.map((batch, batchIndex) => (
                      <tr key={`${categoryIndex}-${itemIndex}-${batchIndex}`} style={isLastBatch(batchIndex) ? { borderBottom: 'none' } : {}}>
                        {batchIndex === 0 && (
                          <td rowSpan={batches.length} style={{ textAlign: 'center' }}>{itemIndex + 1}</td>
                        )}
                        {batchIndex === 0 && (
                          <td rowSpan={batches.length} style={descriptionTdStyle}>
                            <strong>{item.item_name}</strong>
                          </td>
                        )}
                        <td style={{ textAlign: 'center' }}>{batch.quantity}</td>
                        <td style={{ textAlign: 'center' }}>{batch.batch_number}</td>
                        <td style={{ textAlign: 'center' }}>{batch?.expiry ? moment(batch?.expiry).format('DD/MM/YYYY') : ''}</td>
                        <td style={{ textAlign: 'center' }}>{parseFloat(item.price).toFixed(2)}</td>
                        <td style={{ textAlign: 'center' }}>
                          {item?.discount ? `${(((item.discount || 0) / 100) * (item.price || 0) * (batch.quantity || 0)).toFixed(2)} (${item.discount}%)` : '-' }
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          {(
                            (parseFloat(item.price || 0) * parseFloat(batch.quantity || 0)) -
                            (((item.discount || 0) / 100) * (item.price || 0) * (batch.quantity || 0))
                          ).toFixed(2)}
                        </td>
                      </tr>
                    ));
                  })}
                </React.Fragment>
              ))}
            </>
          </tbody>
        </table>
      );
    };

    function convertAmountToWords(amount) {
      //iph todo: 47
      //console.log("amount");
      const toWords = require('number-to-words').toWords;

      const [ringgit, sen] = parseFloat(amount).toFixed(2).split(".");

      const ringgitWords = toWords(parseInt(ringgit)).toUpperCase();
      const senWords = parseInt(sen) > 0 ? ` AND CENTS ${toWords(parseInt(sen)).toUpperCase()}` : '';

      return `${ringgitWords}${senWords}`;
    }

    const renderTotal = () => {
      const items = invoice.item || [];
      const totals = items.reduce((acc, item) => {
        const batches = item.selectedBatches || [];
        batches.forEach((b) => {
          const line = (parseFloat(item.price || 0) * parseFloat(b.quantity || 0));
          const disc = ((item.discount || 0) / 100) * (item.price || 0) * (b.quantity || 0);
          acc.subtotal += line;
          acc.discount += disc;
        });
        return acc;
      }, { subtotal: 0, discount: 0 });
      const total = totals.subtotal - totals.discount;
      const totalInWords = convertAmountToWords(total);

      return (
        <div style={styles.total}>
          <div style={styles.totalAmountSection}>
            <div style={{ marginBottom: '8px' }}>Subtotal: RM {totals.subtotal.toFixed(2)}</div>
            <div style={{ marginBottom: '8px' }}>Discount: RM {totals.discount.toFixed(2)}</div>
            <div style={styles.totalRowCombined}>
              <div style={styles.totalWords}>RINGGIT MALAYSIA {totalInWords} ONLY</div>
              <div style={styles.totalLabelWithBox}>
                <span style={styles.totalLabel}>Total</span>
                <span style={styles.totalValueBox}>RM {total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </div>
            </div>
          </div>
        </div>
      );
    };

    const renderFooter = () => (
      <div style={styles.footer}>
        <div style={styles.notes}>
          <div>Notes :</div>
          <ol>
            <li>
              Please issue cheque made payable to : <strong>IPH PHARMACEUTICALS SDN BHD</strong><br />
              or transfer to our Maybank account no. : <strong>514048620937</strong><br />
              and inform us the details by email : <strong>finance.iphpsb@gmail.com</strong>
            </li>
            <li>Goods sold are not returnable or refundable.</li>
            <li>
              No complaints will be considered unless notified within (2) day upon receipt of the above
              mentioned items
            </li>
          </ol>
        </div>

        <div style={styles.signatures}>
          <div>
            <div>Picked by :</div>
            <div style={styles.signatureLine}></div>
          </div>
          <div>
            <div>Checked by :</div>
            <div style={styles.signatureLine}></div>
          </div>
          <div>
            <div></div>
            <div>This is a computer generated Invoice. No Signature is required.</div>
          </div>
        </div>
      </div>
    );

    return (
      <div className="invoice-container">
        <Card style={styles.card} className="invoice-card" id="invoiceContent">
          <div style={styles.page} data-page="1">
            {renderHeader()}
            <div style={styles.title}>INVOICE / DELIVERY ORDER</div>
            {renderClientInfo(1)}
            {renderItemsTable(
              {
                np: groupedItems,
                poison: groupedItems
              },
            )}
            {renderTotal()}
            {renderFooter()}
          </div>
        </Card>
      </div>
    );
  };

  const renderPOISONDetails = () => {
    // Sample data structure
    const invoice = orderInvoiceDetails;
    if (!invoice) return null;

    // Group items by category
    const groupedItems = invoice.item?.reduce((acc, item) => {
      const category = item.ItemGroup || item.categoryDetails?.Name || '';
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(item);
      return acc;
    }, {});


    const styles = {
      page: {
        fontFamily: 'Arial, sans-serif',
        fontSize: '15px',
        lineHeight: '1.4',
        color: '#000',
        backgroundColor: '#fff',
        width: '300mm',
        minHeight: '297mm',
        margin: '20px auto',
        paddingBottom: '15mm',
        paddingTop: '1mm',
        paddingLeft: '15mm',
        paddingRight: '15mm',
        boxSizing: 'border-box',
        position: 'relative',
      },
      card: {
        width: '300mm',
        minHeight: '297mm',
        border: 'none',
        boxShadow: 'none'
      },
      header: {
        borderBottom: '1px solid #000',
        paddingBottom: '20px',
        marginBottom: '20px'
      },
      headerContent: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start'
      },
      logo: {
        width: '150px',
        height: 'auto'
      },
      companyInfo: {
        textAlign: 'right'
      },
      companyName: {
        fontSize: '16px',
        fontWeight: 'bold',
        marginBottom: '5px'
      },
      title: {
        fontSize: '16px',
        fontWeight: 'bold',
        marginBottom: '0px'
      },
      table: {
        width: '100%',
        borderCollapse: 'collapse',
        marginBottom: '20px'
      },
      th: {
        borderTop: '1px solid #000',
        borderBottom: '1px solid #000',
        padding: '8px',
        textAlign: 'left'
      },
      td: {
        padding: '8px',
        borderBottom: '1px solid #ddd'
      },
      categoryHeader: {
        // backgroundColor: '#f5f5f5',
        padding: '8px',
        fontWeight: 'bold'
      },
      footer: {
      },
      totalSection: {
        display: 'flex',
        justifyContent: 'space-between',
        marginBottom: '20px'
      },
      total: {
        marginTop: '40px',
        paddingTop: '20px',
      },

      totalAmountSection: {
        display: 'flex',
        justifyContent: 'flex-end',
        marginBottom: '20px',
      },

      totalTextContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: '62px',
        borderBottom: '1px solid #000',
        borderTop: '1px solid #000',
        paddingTop: '5px',
        paddingBottom: '5px',
        fontWeight: 'bold',
        fontSize: '14px',
      },

      totalLabel: {
        fontWeight: 'bold',
      },

      totalValue: {
        fontWeight: 'bold',
      },
      notes: {
        marginBottom: '40px'
      },
      signatures: {
        display: 'flex',
        justifyContent: 'space-between',
        marginTop: '60px'
      },
      signatureLine: {
        borderTop: '1px solid #000',
        width: '200px',
        marginTop: '40px'
      }
    };

    const renderHeader = () => (
      <div style={styles.header}>
        <div style={styles.headerContent}>
          <img src={logo} alt="IPHRx" style={styles.logo} />
          <div style={styles.companyInfo}>
            <div style={styles.companyName}>IPH Pharmaceuticals Sdn.Bhd. (1129802-A)</div>
            <div>323, 1st Floor, Jalan Pudu, 55100 Kuala Lumpur, Wilayah Persekutuan , Malaysia</div>
            <div>Mobile No: 010-8726948</div>
            <div>E-mail: iphrx7@gmail.com</div>
            <div>Website: www.iphrx.com.my</div>
          </div>
        </div>
      </div>
    );

    const renderClientInfo = (pageNumber) => (
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
        <div style={{ maxWidth: '48%' }}>
          <div style={{ fontWeight: 'bold', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {rfqDetails?.userDetails?.[0]?.clinic_name}
          </div>
          <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {rfqDetails?.userDetails?.[0]?.name}
          </div>
          <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {rfqDetails?.userDetails?.[0]?.Address}
          </div>
          <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {rfqDetails?.userDetails?.[0]?.email}
          </div>
          <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {rfqDetails?.userDetails?.[0]?.phone}
          </div>
          <div style={{ marginTop: '10px' }}>
            <div style={{ whiteSpace: 'nowrap' }}>Reason for use:</div>
            <div style={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>{rfqDetails?.reason_of_use}</div>
          </div>
        </div>
        <div style={{ maxWidth: '48%' }}>
          <table>
            <tbody>
              <tr>
                <td style={{ whiteSpace: 'nowrap' }}>Invoice / Poison Order No:</td>
                <td style={{ whiteSpace: 'nowrap' }}>{invoice.invoice_number}</td>
              </tr>
              <tr>
                <td style={{ whiteSpace: 'nowrap' }}>Invoice Date:</td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  {moment(invoice.datetime_added).format('DD/MM/YYYY')}
                </td>
              </tr>
              <tr>
                <td style={{ whiteSpace: 'nowrap' }}>Order Number:</td>
                <td style={{ whiteSpace: 'nowrap' }}>{invoice?.order_number || ''}</td>
              </tr>
              <tr>
                <td style={{ whiteSpace: 'nowrap' }}>Order Date:</td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  {moment(invoice.datetime_added).format('DD/MM/YYYY') || ''}
                </td>
              </tr>
              <tr>
                <td style={{ whiteSpace: 'nowrap' }}>Credit Term:</td>
                <td style={{ whiteSpace: 'nowrap' }}>{formatPaymentTerm(orderInvoiceDetails?.paymentTerms || rfqDetails?.paymentTerms)}</td>
              </tr>
              {/* <tr>
                 <td style={{ whiteSpace: 'nowrap' }}>Page:</td>
                 <td style={{ whiteSpace: 'nowrap' }}>{pageNumber} of 2</td>
               </tr> */}
            </tbody>
          </table>
        </div>
      </div>


    );

    const renderItemsTable = (items, showTotal = false) => (
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>#</th>
            <th style={styles.th}>Item (Description)</th>
            <th style={styles.th}>Qty</th>
            <th style={styles.th}>Batch No</th>
            <th style={styles.th}>Expiry</th>
            <th style={styles.th}>U/Price (RM)</th>
            <th style={styles.th}>Discount (RM)</th>
            <th style={styles.th}>Amount (RM)</th>
          </tr>
        </thead>
        <tbody>
          <>
            <tr>
              <td colSpan="8" style={styles.categoryHeader}>POISON</td>
            </tr>
            {Object.entries(groupedItems).map(([category, items], categoryIndex) => (
              <React.Fragment key={categoryIndex}>
                {items.map((item, itemIndex) => (
                  item.selectedBatches?.map((batch, batchIndex) => (
                    <tr key={`${categoryIndex}-${itemIndex}-${batchIndex}`} style={batchIndex === (item.selectedBatches.length - 1) ? { borderBottom: 'none' } : {}}>
                      {batchIndex === 0 && (
                        <td rowSpan={item.selectedBatches?.length || 1} style={{ textAlign: 'center' }}>{itemIndex + 1}</td>
                      )}
                      {batchIndex === 0 && (
                        <td rowSpan={item.selectedBatches?.length || 1} style={{ maxWidth: '28%', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                          <strong>{item.item_name}</strong>
                        </td>
                      )}
                      <td style={{ textAlign: 'center' }}>{batch.quantity}</td>
                      <td style={{ textAlign: 'center' }}>{batch.batch_number}</td>
                      <td style={{ textAlign: 'center' }}>{batch?.expiry ? moment(batch.expiry).format('DD/MM/YYYY') : ''}</td>
                      <td style={{ textAlign: 'center' }}>{parseFloat(item.price).toFixed(2)}</td>
                      <td style={{ textAlign: 'center' }}>
                        {item?.discount ? `${(((item.discount || 0) / 100) * (item.price || 0) * (batch.quantity || 0)).toFixed(2)} (${item.discount}%)` : '-' }
                      </td>
                      <td style={{ textAlign: 'center' }}> {(
                        (parseFloat(item.price || 0) * parseFloat(batch.quantity || 0)) -
                        (((item.discount || 0) / 100) * (item.price || 0) * (batch.quantity || 0))
                      ).toFixed(2)}</td>
                    </tr>
                  ))
                ))}
              </React.Fragment>
            ))}
          </>

        </tbody>
      </table >
    );
    const renderTotal = () => (
      <div style={styles.total}>
        <div style={styles.totalAmountSection}>
          {(() => {
            const items = invoice.item || [];
            const totals = items.reduce((acc, item) => {
              const batches = item.selectedBatches || [];
              batches.forEach((b) => {
                const line = (parseFloat(item.price || 0) * parseFloat(b.quantity || 0));
                const disc = ((item.discount || 0) / 100) * (item.price || 0) * (b.quantity || 0);
                acc.subtotal += line;
                acc.discount += disc;
              });
              return acc;
            }, { subtotal: 0, discount: 0 });
            const total = totals.subtotal - totals.discount;
            return (
              <>
                <div style={styles.totalTextContainer}>
                  <span style={styles.totalLabel}>Total &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</span>
                  <span style={styles.totalValue}>&nbsp;RM {total.toFixed(2)}</span>
                </div>
              </>
            );
          })()}
        </div>
      </div>
    );

    const renderFooter = () => (
      <div style={styles.footer}>
        <div style={styles.totalSection}>
          {(() => {
            const items = (orderInvoiceDetails && orderInvoiceDetails.item) ? orderInvoiceDetails.item : [];
            const totals = items.reduce((acc, item) => {
              const batches = item.selectedBatches || [];
              batches.forEach((b) => {
                const line = (parseFloat(item.price || 0) * parseFloat(b.quantity || 0));
                const disc = ((item.discount || 0) / 100) * (item.price || 0) * (b.quantity || 0);
                acc.subtotal += line;
                acc.discount += disc;
              });
              return acc;
            }, { subtotal: 0, discount: 0 });
            const total = totals.subtotal - totals.discount;
            return (
              <div>
                <div>Subtotal: <strong>RM {totals.subtotal.toFixed(2)}</strong></div>
                <div>Discount: <strong>RM {totals.discount.toFixed(2)}</strong></div>
                <div style={{ marginTop: '6px' }}>Total Amount Payable : <strong>RM {total.toFixed(2)}</strong></div>
              </div>
            );
          })()}
        </div>

        <div style={{ marginBottom: '20px' }}>
          <div>Issued by:</div>
          <div style={styles.signatureLine}></div>
        </div>

        <div style={styles.notes}>
          <div>Notes :</div>
          <ol>
            <li>
              Please issue cheque made payable to : <strong>IPH PHARMACEUTICALS SDN BHD</strong><br />
              or transfer to our Maybank account no. : <strong>514048620937</strong><br />
              and inform us the details by email : <strong>finance.iphpsb@gmail.com</strong>
            </li>
            <li>Goods sold are not returnable or refundable.</li>
            <li>
              No complaints will be considered unless notified within (2) day upon receipt of the above
              mentioned items
            </li>
          </ol>
        </div>

        <div style={styles.signatures}>
          <div>
            <div>Picked by :</div>
            <div style={styles.signatureLine}></div>
          </div>
          <div>
            <div>Checked by :</div>
            <div style={styles.signatureLine}></div>
          </div>
          <div>
            <div>Signature & Co stamp Date:</div>
            <div style={styles.signatureLine}></div>
          </div>
        </div>
      </div>
    );

    return (
      <div className="invoice-container">
        <Card style={styles.card} className="invoice-card" id="invoiceContent">
          <div style={styles.page} data-page="1">
            {renderHeader()}
            <div style={styles.title}>INVOICE / POISON ORDER</div>
            {renderClientInfo(1)}
            {renderItemsTable(
              {
                np: groupedItems,
                poison: groupedItems
              },
            )}
            {renderTotal()}
            {renderFooter()}
          </div>
        </Card>
      </div>
    );
  };


  const renderMessagingTab = () => (
    <Card className="mb-3">
      <CardBody className="p-0">
        <div className="chat-container">
          <div
            className="messages-container"
            ref={chatContainerRef}
          >
            {messages?.map((msg, index) => (
              <div key={index} className={`message-wrapper ${msg.sender_type === 'doctor' ? 'received' : 'sent'}`}>
                <div className="message-content">
                  <div className="message-sender">{msg.sender_type === 'admin' ? 'admin' : 'doctor'}</div>
                  <div className="message-text">
                    {msg.message}
                  </div>
                  <div className="message-time">
                    {moment(msg.date).format('DD-MM-YYYY, h:mm A')}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="message-input">
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  sendMessage();
                }
              }}
              placeholder="Type a message"
            />
            <Button color="primary" onClick={sendMessage}>
              <FontAwesomeIcon icon={faPaperPlane} />
            </Button>
          </div>
        </div>
      </CardBody>
    </Card>
  );

  const renderTrackingContentDEMO = () => {
    if (!orderTrackingDetails || orderTrackingDetails?.length === 0) {
      return <div>No tracking information available</div>;
    }

    return (
      <div style={{
        backgroundColor: 'white',
        borderRadius: '10px',
        padding: '20px',
        boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
      }}>
        {orderTrackingDetails?.map((step, index) => (
          <div key={index} style={{
            display: 'flex',
            marginBottom: '20px',
            position: 'relative'
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              backgroundColor: getStatusColor(step.status),
              borderRadius: '50%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              marginRight: '20px',
              zIndex: 1
            }}>
              <FontAwesomeIcon icon={getIconForStatus(step.status)} style={{ color: 'white' }} />
            </div>
            {index !== orderTrackingDetails.length - 1 && (
              <div style={{
                position: 'absolute',
                left: '20px',
                top: '40px',
                bottom: '-20px',
                width: '2px',
                borderLeft: `2px dashed ${getStatusColor(step.status)}`,
                zIndex: 0
              }} />
            )}
            <div style={{ flex: 1 }}>
              <h3 style={{ margin: '0 0 5px 0', color: getStatusColor(step.status) }}>{formatStatus(step.status)}</h3>
              <p style={{ margin: '0', color: '#666' }}>
                <FontAwesomeIcon icon={faClock} style={{ marginRight: '5px' }} />
                {moment(step.date_time).format('DD-MM-YYYY, h:mm:ss a')}
              </p>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const downloadInvoice = () => {
    const input = document.getElementById('invoiceContent');
    setLoading(true);
    html2canvas(input)
      .then((canvas) => {
        const imgData = canvas.toDataURL('image/png');
        const pdf = new jsPDF();
        const imgProps = pdf.getImageProperties(imgData);
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
        pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
        pdf.save(`invoice_pdf.pdf`);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error generating PDF:", error);
        setLoading(false);
        Alerts.swalErrorAlert(t("Failed to download invoice"));
      });
  };
  const downloadPaymentReceipt = () => {
    const input = document.getElementById('paymentReceiptContent');
    // Avoid activating LoadingOverlay; it gets captured as a grey mask by html2canvas
    html2canvas(input, { backgroundColor: '#ffffff', scale: 2, useCORS: true })
      .then((canvas) => {
        const imgData = canvas.toDataURL('image/png');
        const pdf = new jsPDF();
        const imgProps = pdf.getImageProperties(imgData);
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
        pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
        pdf.save(`payment_receipt.pdf`);
      })
      .catch((error) => {
        console.error("Error generating PDF:", error);
        Alerts.swalErrorAlert(t("Failed to download payment receipt"));
      });
  };

  const downloadCreditNote = () => {
    const input = document.getElementById('creditNoteContent');
    setLoading(true);
    html2canvas(input)
      .then((canvas) => {
        const imgData = canvas.toDataURL('image/png');
        const pdf = new jsPDF();
        const imgProps = pdf.getImageProperties(imgData);
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
        pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
        pdf.save(`CreditNote.pdf`);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error generating PDF:", error);
        setLoading(false);
        Alerts.swalErrorAlert(t("Failed to download credit note"));
      });
  };

  const renderPaymentsDetails = () => {
    const payment = orderPaymentDetails[0];
    if (!payment) return null;


    return (
      <div className="payment-receipt-container">
        <Card className="payment-receipt-card" id="paymentReceiptContent">
          <div className="payment-receipt-header">
            <Row>
              <Col md={8}>
                <img src={logo} alt="IPHRx" className="company-logo" />
                <p className="company-details">323, 1st Floor, Jalan Pudu, 55100 Kuala Lumpur, Wilayah Persekutuan , Malaysia</p>
                <p className="company-details">iphrx7@gmail.com</p>
                <p className="company-details">+60 1234567890</p>
              </Col>
              <Col md={4} className="text-end">
                <h1 className="payment-receipt-title">PAYMENT RECEIPT</h1>
                <table className="payment-info-table">
                  <tbody>
                    <tr>
                      <th>Order No.:</th>
                      <td>{payment.order_no || orderInvoiceDetails?.order_number || '-'}</td>
                    </tr>
                    <tr>
                      <th>Date:</th>
                      <td>{moment(payment.payment_date).format('DD/MM/YYYY')}</td>
                    </tr>
                    <tr>
                      <th>Status:</th>
                      <td>{payment.status}</td>
                    </tr>
                  </tbody>
                </table>
              </Col>
            </Row>
          </div>

            {/* Centered PAYMENT DETAILS heading spanning full width */}
            <div className="d-flex justify-content-center mt-2 mb-2" style={{ width: '100%' }}>
              <h3 className="section-title" style={{ margin: 0, textAlign: 'center' }}>PAYMENT DETAILS</h3>
            </div>

          <div className="payment-receipt-body">
            <Row>
              <Col md={6}>
                <div className="bill-to-section">
                  <h3 className="section-title">BILL TO</h3>
                  <p className="client-name">{rfqDetails?.userDetails?.[0]?.name}</p>
                  <p className="client-details">{rfqDetails?.userDetails?.[0]?.email}</p>
                  <p className="client-details">{rfqDetails?.userDetails?.[0]?.Address}</p>
                </div>
              </Col>
              <Col md={6}>
                <div className="payment-details-section text-end">
                  <p className="payment-details"><strong>Transaction ID:</strong> {payment.transition_id}</p>
                </div>
              </Col>
            </Row>

          

            <div className="payment-items">
              <table>
                <thead>
                  <tr>
                    <th>Order No.</th>
                    <th>Invoice No.</th>
                    <th>Paid Amount</th>
                    <th>Total Amount</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{payment.order_no || orderInvoiceDetails?.order_number || '-'}</td>
                    <td>{payment.invoice_number}</td>
                    <td>RM {parseFloat(payment.amount).toFixed(2)}</td>
                    <td>RM {parseFloat(payment.total_amount).toFixed(2)}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="payment-summary">
              <Row>
                <Col md={8}>
                  <div className="payment-notes">
                    <h4>Notes</h4>
                    <p>Thank you for your payment</p>
                  </div>
                </Col>
                <Col md={4}>
                  <table className="summary-table">
                    <tbody>
                      <tr className="total-row">
                        <th>Total Paid:</th>
                        <td>RM {parseFloat(payment.amount).toFixed(2)}</td>
                      </tr>
                    </tbody>
                  </table>
                </Col>
              </Row>
            </div>
          </div>

          <div className="payment-receipt-footer">
            <p>If you have any questions about this payment, please contact us at <strong>finance.iphpsb@gmail.com</strong></p>
            <p>Thank you for your business</p>
          </div>
        </Card>



        <style>{`
          .payment-receipt-container {
            background-color: #f8f9fa;
            padding: 20px;
          }
          .logo {
            width: 150px;
            height: auto;
          }
          .payment-receipt-card {
            background-color: white;
            border: none;
            box-shadow: 0 0 30px rgba(0,0,0,0.1);
            padding: 40px;
            margin-bottom: 20px;
          }
          .payment-receipt-header {
            border-bottom: 2px solid #3D607A;
            padding-bottom: 20px;
            margin-bottom: 30px;
          }
          .company-logo {
            max-width: 150px;
            height: auto;
            margin-bottom: 5px;
          }
          .company-name {
            color: #3D607A;
            font-size: 24px;
            font-weight: 600;
            margin-bottom: 10px;
          }
          .company-details {
            color: #6c757d;
            margin: 0;
            line-height: 1.5;
          }
          .payment-receipt-title {
            color: #3D607A;
            font-size: 36px;
            font-weight: 700;
            margin: 0 0 20px 0;
          }
          .payment-info-table {
            width: 100%;
            margin-top: 10px;
          }
          .payment-info-table th, .payment-info-table td {
            padding: 1px;
            text-align: right;
          }
          .payment-info-table th {
            color: #3D607A;
            font-weight: 600;
            padding-right: 1px;
          }
          .payment-info-table td {
            padding-left: 1px;
          }
          .payment-receipt-body {
            margin-top: 30px;
          }
          .section-title {
            color: #3D607A;
            font-size: 18px;
            font-weight: 600;
            margin-bottom: 15px;
            text-transform: uppercase;
          }
          .client-name {
            font-size: 18px;
            font-weight: 600;
            margin-bottom: 5px;
          }
          .client-details, .payment-details {
            color: #6c757d;
            margin: 0;
            line-height: 1.5;
          }
          .payment-details-section {
            text-align: right;
          }
          .payment-items {
            margin-top: 30px;
            margin-bottom: 30px;
          }
          .payment-items table {
            width: 100%;
            border-collapse: separate;
            border-spacing: 0;
          }
          .payment-items th, .payment-items td {
            border: 1px solid #dee2e6;
            padding: 10px;
            text-align: left;
          }
          .payment-items th {
            background-color: #3D607A;
            color: white;
            font-weight: 600;
          }
          .payment-items tr:nth-child(even) {
            background-color: #f8f9fa;
          }
          .summary-table {
            width: 100%;
          }
          .summary-table th, .summary-table td {
            padding: 4px;
            text-align: right;
          }
          .summary-table th {
            color: #3D607A;
            font-weight: 600;
            padding-right: 1px;
          }
          .summary-table td {
            padding-left: 1px;
          }
          .total-row {
            font-size: 18px;
            font-weight: 700;
            color: #3D607A;
          }
          .payment-notes {
            background-color: #f8f9fa;
            border-left: 4px solid #3D607A;
            padding: 15px;
          }
          .payment-notes h4 {
            color: #3D607A;
            margin-bottom: 10px;
          }
          .payment-receipt-footer {
            margin-top: 40px;
            text-align: center;
            color: #6c757d;
            font-size: 14px;
          }
        `}</style>
      </div>
    );
  };

  const renderCreditNoteTab = () => {
    // Sample data structure
    const creditNote = orderCreditNoteDetails;
    if (!creditNote) return null;

    // Group items by category and filter out items with zero returned quantity
    const groupedItems = creditNote.items?.reduce((acc, item) => {
      if (item.returned_quantity > 0) {
        const category = item.ItemGroup || item.categoryDetails?.Name || '';
        if (!acc[category]) {
          acc[category] = [];
        }
        acc[category].push(item);
      }
      return acc;
    }, {});



    const styles = {
      page: {
        fontFamily: 'Arial, sans-serif',
        fontSize: '15px',
        lineHeight: '1.4',
        color: '#000',
        backgroundColor: '#fff',
        width: '300mm',
        minHeight: '297mm',
        margin: '20px auto',
        paddingBottom: '15mm',
        paddingTop: '1mm',
        paddingLeft: '15mm',
        paddingRight: '15mm',
        boxSizing: 'border-box',
        position: 'relative',
      },
      card: {
        width: '300mm',
        minHeight: '297mm',
      },
      header: {
        borderBottom: '1px solid #000',
        paddingBottom: '20px',
        marginBottom: '20px'
      },
      headerContent: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start'
      },
      logo: {
        width: '150px',
        height: 'auto'
      },
      companyInfo: {
        textAlign: 'right'
      },
      companyName: {
        fontSize: '16px',
        fontWeight: 'bold',
        marginBottom: '5px'
      },
      title: {
        fontSize: '16px',
        fontWeight: 'bold',
        marginBottom: '20px'
      },
      table: {
        width: '100%',
        borderCollapse: 'collapse',
        marginBottom: '20px'
      },
      th: {
        borderTop: '1px solid #000',
        borderBottom: '1px solid #000',
        padding: '8px',
        textAlign: 'left'
      },
      td: {
        padding: '8px',
        borderBottom: '1px solid #ddd'
      },
      categoryHeader: {
        backgroundColor: '#f5f5f5',
        padding: '8px',
        fontWeight: 'bold'
      },

      totalSection: {
        display: 'flex',
        justifyContent: 'space-between',
        marginBottom: '20px'
      },


      total: {
        marginTop: '40px',
        paddingTop: '20px',
      },

      totalAmountSection: {
        display: 'flex',
        justifyContent: 'flex-end',
        marginBottom: '20px',
      },

      totalTextContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        borderBottom: '1px solid #000',
        borderTop: '1px solid #000',
        paddingTop: '5px',
        paddingBottom: '5px',
        fontWeight: 'bold',
        fontSize: '14px',
      },

      totalLabel: {
        fontWeight: 'bold',
      },

      totalValue: {
        fontWeight: 'bold',
      },


      notes: {
        marginBottom: '40px'
      },
      signatures: {
        display: 'flex',
        justifyContent: 'space-between',
        marginTop: '60px'
      },
      signatureLine: {
        borderTop: '1px solid #000',
        width: '200px',
        marginTop: '40px'
      }
    };

    const renderHeader = () => (
      <div style={styles.header}>
        <div style={styles.headerContent}>
          <img src={logo} alt="IPHRx" style={styles.logo} />
          <div style={styles.companyInfo}>
            <div style={styles.companyName}>IPH Pharmaceuticals Sdn.Bhd. (1129802-A)</div>
            <div>323, 1st Floor, Jalan Pudu, 55100 Kuala Lumpur, Wilayah Persekutuan , Malaysia</div>
            <div>Mobile No: 010-8726948</div>
            <div>E-mail: iphrx7@gmail.com</div>
            <div>Website: www.iphrx.com.my</div>
          </div>
        </div>
      </div>
    );

    const renderClientInfo = (pageNumber) => (
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
        <div style={{ maxWidth: '48%' }}>
          <div style={{ fontWeight: 'bold', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {rfqDetails?.userDetails?.[0]?.clinic_name}
          </div>
          <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {rfqDetails?.userDetails?.[0]?.name}
          </div>
          <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {rfqDetails?.userDetails?.[0]?.Address}
          </div>
          <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {rfqDetails?.userDetails?.[0]?.email}
          </div>
          <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {rfqDetails?.userDetails?.[0]?.phone}
          </div>
          <div style={{ marginTop: '10px' }}>
            <div style={{ whiteSpace: 'nowrap' }}>Reason:</div>
            <div style={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>{rfqDetails?.return_reason}</div>
          </div>
        </div>
        <div style={{ maxWidth: '48%' }}>
          <table>
            <tbody>
              <tr>
                <td style={{ whiteSpace: 'nowrap' }}>Credit Note No.:</td>
                <td style={{ whiteSpace: 'nowrap' }}>{creditNote.credit_note_number}</td>
              </tr>
              <tr>
                <td style={{ whiteSpace: 'nowrap' }}>Credit Note Date:</td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  {moment(creditNote.datetime_added).format('DD MM YYYY')}
                </td>
              </tr>

              <tr>
                <td style={{ whiteSpace: 'nowrap' }}>Ref. Inv No.:</td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  {orderInvoiceDetails?.invoice_number || 'N/A'}
                </td>
              </tr>
              <tr>
                <td style={{ whiteSpace: 'nowrap' }}>Page:</td>
                <td style={{ whiteSpace: 'nowrap' }}>1 of 1</td>
              </tr>
              {/* <tr>
                 <td style={{ whiteSpace: 'nowrap' }}>Page:</td>
                 <td style={{ whiteSpace: 'nowrap' }}>{pageNumber} of 2</td>
               </tr> */}
            </tbody>
          </table>
        </div>
      </div>


    );

    const renderItemsTable = (items, showTotal = false) => (
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>Item</th>
            <th style={styles.th}>Description</th>
            <th style={styles.th}>Qty</th>
            <th style={styles.th}>Batch No</th>
            <th style={styles.th}>Expiry</th>
            <th style={styles.th}>U/Price (RM)</th>
            <th style={styles.th}>Amount (RM)</th>
          </tr>
        </thead>
        <tbody>
          {/* {items.np && items.np.length > 0 && ( */}
          <>
            <tr>
              <td colSpan="7" style={styles.categoryHeader}>POISON</td>
            </tr>
            {Object.entries(groupedItems).map(([category, items], categoryIndex) => (
              <React.Fragment key={categoryIndex}>
                {items.map((item, itemIndex) => (
                  item.selectedBatches?.map((batch, batchIndex) => (
                    <tr key={`${categoryIndex}-${itemIndex}-${batchIndex}`}>
                      {batchIndex === 0 && itemIndex === 0 && (
                        <td rowSpan={items.reduce((sum, item) => sum + (item.selectedBatches?.length || 1), 0)}>
                          {category}
                        </td>
                      )}
                      {batchIndex === 0 && (
                        <td rowSpan={item.selectedBatches?.length || 1}>
                          <strong>{item.item_name}</strong>
                        </td>
                      )}
                      <td>{batch.returned_quantity}</td>
                      <td>{batch.batch_number}</td>
                      <td>{batch?.expiry ? moment(batch.expiry).format('DD/MM/YYYY') : ''}</td>
                      <td>{parseFloat(item.price).toFixed(2)}</td>
                      <td>{(parseFloat(item.price) * parseFloat(batch.returned_quantity)).toFixed(2)}</td>
                    </tr>
                  ))
                ))}
              </React.Fragment>
            ))}
          </>
          {/* <tr>
              <td colSpan="5"></td>
              <td style={{ ...styles.td, fontWeight: 'bold', borderTop: '1px solid #000' }}>Total</td>
              <td style={{ ...styles.td, fontWeight: 'bold', borderTop: '1px solid #000' }}>RM 790.80</td>
            </tr> */}
        </tbody>
      </table>
    );
    const renderTotal = () => (
      <div style={styles.total}>
        <div style={styles.totalAmountSection}>
          <div style={styles.totalTextContainer}>
            <span style={styles.totalLabel}>Total &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</span>
            <span style={styles.totalValue}>&nbsp;RM {creditNote.total_amount}</span>
          </div>
        </div>
      </div>
    );

    const renderFooter = () => (
      <div style={styles.footer}>
        <div style={styles.totalSection}>
          {(() => {
            const items = (orderInvoiceDetails && orderInvoiceDetails.item) ? orderInvoiceDetails.item : [];
            const totals = items.reduce((acc, item) => {
              const batches = item.selectedBatches || [];
              batches.forEach((b) => {
                const line = (parseFloat(item.price || 0) * parseFloat(b.quantity || 0));
                const disc = ((item.discount || 0) / 100) * (item.price || 0) * (b.quantity || 0);
                acc.subtotal += line;
                acc.discount += disc;
              });
              return acc;
            }, { subtotal: 0, discount: 0 });
            const total = totals.subtotal - totals.discount;
            return (
              <div>
                <div>Subtotal: <strong>RM {totals.subtotal.toFixed(2)}</strong></div>
                <div>Discount: <strong>RM {totals.discount.toFixed(2)}</strong></div>
                <div style={{ marginTop: '6px' }}>Total Amount Payable : <strong>RM {total.toFixed(2)}</strong></div>
              </div>
            );
          })()}
        </div>

        <div style={{ marginBottom: '20px' }}>
          <div>Issued by:</div>
          <div style={styles.signatureLine}></div>
        </div>

        <div style={styles.notes}>
          <div>Notes :</div>
          <ol>
            <li>
              Please issue cheque made payable to : <strong>IPH PHARMACEUTICALS SDN BHD</strong><br />
              or transfer to our Maybank account no. : <strong>514048620937</strong><br />
              and inform us the details by email : <strong>finance.iphpsb@gmail.com</strong>
            </li>
            <li>Goods sold are not returnable or refundable.</li>
            <li>
              No complaints will be considered unless notified within (2) day upon receipt of the above
              mentioned items
            </li>
          </ol>
        </div>

        <div style={styles.signatures}>
          <div>
            <div>Picked by :</div>
            <div style={styles.signatureLine}></div>
          </div>
          <div>
            <div>Checked by :</div>
            <div style={styles.signatureLine}></div>
          </div>
          <div>
            <div>Signature & Co stamp Date:</div>
            <div style={styles.signatureLine}></div>
          </div>
        </div>
      </div>
    );

    return (
      <div className="credit-note-container">
        <Card style={styles.card} className="invoice-card" id="creditNoteContent">
          <div style={styles.page} data-page="1">
            {renderHeader()}
            <div style={styles.title}>Credit Note</div>
            {renderClientInfo(1)}
            {renderItemsTable(
              {
                np: groupedItems,
                poison: groupedItems
              },
            )}
            {renderTotal()}
            {renderFooter()}
          </div>
        </Card>
      </div>
    );
  };

  // Helper function to calculate total amount
  const getTotalAmount = (groupedItems) => {
    let total = 0
    Object.entries(groupedItems).forEach(([category, items]) => {
      items.forEach((item) => {
        total += Number.parseFloat(item.price * item.returned_quantity)
      })
    })
    return total
  }

  const amountInWords = (amount) => {
    const rounded = Math.round(amount)
    return `${toWords(rounded)} only`
  }
  const ApproveReturn = async () => {
    try {
      setLoading(true);
      var payload = {
        functionName: "AcceptReturnItems",
        postData: {
          _id: id,
        }
      }
      const response = await DashboardService.AcceptReturnItems(payload);
      if (response.data.status) {
        Alerts.swalSuccessAlert(t("Items Returned successfully"));
        fetchRfqDetails();
      } else {
        Alerts.swalErrorAlert(response.data.message || t("Failed to accept Order"));
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error("Error accepting Order:", error);
      Alerts.swalErrorAlert(t("An error occurred while accepting the Order"));
    }
  };

  const renderReturnTab = () => {
    if (!rfqDetails) return null;

    const handleItemChange = (itemIndex, batchIndex, value) => {
      const newItems = [...rfqDetails.items];
      newItems[itemIndex].selectedBatches[batchIndex].returned_quantity = parseInt(value);
      setRfqDetails({ ...rfqDetails, items: newItems });
    };



    return (
      <div className="return-tab-container">
        <Card className="return-card">
          <div className="p-4">
            <Form>
              <h5 className="mb-3 fw-bold">{t("Return Item(s)")}</h5>
              <FormGroup className="mb-3">
                <Label for="returnReason">{t("Reason for Return")}</Label>
                <Input
                  type="textarea"
                  name="returnReason"
                  id="returnReason"
                  disabled
                  style={{ backgroundColor: "white" }}
                  defaultValue={rfqDetails?.return_reason || returnReason}
                  onChange={(e) => setReturnReason(e.target.value)}
                  placeholder={t("Please provide a reason for returning the items")}
                  rows="3"
                  required
                />
              </FormGroup>
              <div className="item-list mb-3">
                <Row className="mb-2 text-muted">
                  <Col md={2}>{t("Item(s)")}</Col>
                  <Col md={2}>{t("Batch")}</Col>
                  <Col md={2}>{t("Expiry")}</Col>
                  <Col md={2}>{t("Quantity")}</Col>
                  <Col md={2}>{t("Returned Quantity")}</Col>
                  <Col md={2}>{t("Remaining Quantity")}</Col>
                </Row>
                {rfqDetails.items.map((item, itemIndex) => (
                  item.selectedBatches.map((batch, batchIndex) => (
                    <Row key={`${itemIndex}-${batchIndex}`} className="mb-2 align-items-center">
                      <Col md={2}>{item.itemDetails?.item_name}</Col>
                      <Col md={2}>{batch.batch_number}</Col>
                      <Col md={2}>{batch?.expiry ? moment(batch.expiry).format("DD-MM-YYYY") : ''}</Col>
                      <Col md={2}>{batch.quantity}</Col>
                      <Col md={2}>
                        <Input
                          type="number"
                          min="0"
                          disabled={true}
                          max={batch.quantity}
                          readOnly={rfqDetails?.return_status && rfqDetails?.return_status !== "pending"}
                          value={batch.returned_quantity || ''}
                          onChange={(e) => handleItemChange(itemIndex, batchIndex, e.target.value)}
                        />
                      </Col>
                      <Col md={2}>{batch.quantity - (batch.returned_quantity || 0)}</Col>
                    </Row>
                  ))
                ))}
              </div>
              <div className="d-flex justify-content-end mt-3">
                {rfqDetails?.return_status === "pending" && (
                  <Button onClick={ApproveReturn} color="success" className="px-4">
                    {t("Approve Return")}
                  </Button>
                )}
              </div>
            </Form>
          </div>
        </Card>
      </div>
    );
  };



  return (
    <>
      <div className="app-main__outer">
        <div className="app-main__inner">
          <LoadingOverlay active={loading} spinner={<DNALoader />}>
            <div className="header">
              <Button color="link" className="p-0 text-decoration-none" onClick={() => navigate(-1)}>
                <FaArrowLeft className="me-2" />
                <span className="text-dark fw-bold">{t("New Order")}</span>
              </Button>

              {/* <div className="actions">
                {rfqDetails?.status === "Review" && (
                  <button onClick={finalIssueQuotation} className="accept-button">{t("Review (Issue Now)")}</button>
                )}
                {rfqDetails?.status === "requested" && (
                  <button onClick={IssueQuotation} className="accept-button">{t("Verify Invoice")}</button>
                )}
                {rfqDetails?.status === "accepted" && (
                  <button className="accept-button">{t("Quotation Accepted")}</button>
                )}
                {rfqDetails?.status === "rejected" && (
                  <>
                    <button onClick={handleEditQuotation} className="edit-button">
                      <FaEdit className="me-2" />
                      {t("Edit Quotation")}
                    </button>
                  </>
                )}
                {!["Review", "requested", "accepted", "rejected"].includes(rfqDetails?.status) && (
                  <button className="reject-button">{t("Pending")}</button>
                )}
              </div>
            </div> */}
              <div className="actions">
                {/* Edit Order Icon - Always visible for certain statuses */}
                {((rfqDetails?.status === 'requested') || (rfqDetails?.status === 'Back Order') || (rfqDetails?.status === 'rejected')) && (
                  <button 
                    onClick={() => navigate(`/edit_rfqs/${rfqDetails._id}`, { state: { from: 'quotation_details' } })}
                    className="edit-rfq-button me-2"
                    title={t("Edit Order")}
                  >
                    <FaEdit className="me-1" />
                    {t("Edit Order")}
                  </button>
                )}

                {activeTab === 'invoice_details' &&
                  <Button disabled={orderInvoiceDetails?.autoCount_status !== 'submitted'} color="primary" onClick={downloadInvoice} className="accept-button">
                    <FaDownload className="me-2" />{orderInvoiceDetails?.autoCount_status !== 'submitted' ? "invoice is being generated and will be emailed once ready" : "Download Invoice"}
                  </Button>
                }

                {activeTab === 'return' ?
                  rfqDetails?.return_status === "pending" ? (
                    <button onClick={ApproveReturn} className="accept-button">{t("Approve")}</button>
                  ) : rfqDetails?.return_status === "rejected" ? (
                    <button className="rejected-button">{t("Items Not Returned")}</button>
                  ) : rfqDetails?.return_status === "accepted" ? (
                    <button className="processing-button">{t("Items Returned")}</button>
                  ) : (
                    <button disabled className="pending-button">{t("Pending")}</button>
                  ) : ''
                }
                {activeTab === 'payment_details' &&
                  <Button color="primary" onClick={downloadPaymentReceipt} className="accept-button">
                    <FaDownload className="me-2" /> Download Payment Receipt
                  </Button>
                }
                {activeTab === 'credit_note' &&
                  <Button color="primary" onClick={downloadCreditNote} className="accept-button">
                    <FaDownload className="me-2" /> Download Credit Note
                  </Button>
                }
                {rfqDetails?.status === "Review" ? (
                  <button onClick={finalIssueQuotation} className="accept-button">{t("Review (Issue Now)")}</button>
                ) : (rfqDetails?.status === "requested" || rfqDetails?.status === "Back Order") ? (
                  <button onClick={IssueQuotation} className="accept-button">{t("Verify Invoice")}</button>
                ) : rfqDetails?.status === "accepted" ? (
                  <button className="accept-button">{t("Quotation Accepted")}</button>
                ) : rfqDetails?.status === "rejected" ? (
                  <>
                    <button onClick={handleEditQuotation} className="edit-button">
                      <FaEdit className="me-2" />
                      {t("Edit Quotation")}
                    </button>
                  </>
                ) : rfqDetails?.status === "accepted" ? (
                  <button className="accept-button">{t("Quotation Accepted")}</button>
                ) : rfqDetails?.status === "rejected" || rfqDetails?.status === "cancelled" ? (
                  <button className="rejected-button">{t("Quotation Rejected")}</button>
                ) : rfqDetails?.status === "processing" ? (
                  <button className="processing-button">{t("Processing")}</button>
                ) : rfqDetails?.status === "confirmed" ? (
                  <button className="processing-button">{t("Confirmed")}</button>
                ) : rfqDetails?.status === "Waiting for Approval" ? (
                  <>
                    <button className="approval-button">{t("Waiting for Approval")}</button>
                  </>
                ) : rfqDetails?.status === "Approved" ? (
                  <button onClick={IssueQuotation} className="Approved-button">{t("Approved (Issue Quotation)")}</button>

                ) : rfqDetails?.status === "in_transit" ? (
                  <button className="in-transit-button">{t("In Transit")}</button>
                ) : rfqDetails?.status === "delivered" ? (
                  <button className="delivered-button">{t("Delivered")}</button>
                ) : (
                  <button disabled className="pending-button">{t("Pending")}</button>
                )}
              </div>
            </div>
            <h4 className="mb-4 fw-bold">{t("Order ID")} #{rfqDetails?.rfq_id}</h4>

            <div className="order-tabs mb-4">
              <button
                className={`tab-button ${activeTab === 'details' ? 'active' : ''}`}
                onClick={() => setActiveTab('details')}
              >
                <span className="tab-icon">📋</span>
                Details
              </button>
              {orderTrackingDetails?.length !== 0 &&
                <button
                  className={`tab-button ${activeTab === 'tracking' ? 'active' : ''}`}
                  onClick={() => setActiveTab('tracking')}
                >
                  <span className="tab-icon">🚚</span>
                  Order Tracking
                </button>
              }
              <button
                className={`tab-button ${activeTab === 'messaging' ? 'active' : ''}`}
                onClick={() => {
                  setActiveTab('messaging')
                  fetchMessages()
                }}
              >
                <span className="tab-icon">💬</span>
                Messages
              </button>
              {orderInvoiceDetails?.autoCount_status === 'submitted' && orderInvoiceDetails !== null &&
                <button
                  className={`tab-button ${activeTab === 'invoice_details' ? 'active' : ''}`}
                  onClick={() => setActiveTab('invoice_details')}
                >
                  <span className="tab-icon">📄</span>
                  Invoice
                </button>
              }
              {orderPaymentDetails?.length !== 0 &&
                <button
                  className={`tab-button ${activeTab === 'payment_details' ? 'active' : ''}`}
                  onClick={() => setActiveTab('payment_details')}
                >
                  <span className="tab-icon">💳</span>
                  Payment
                </button>
              }
              {rfqDetails?.status === 'delivered' && orderInvoiceDetails !== null && orderPaymentDetails?.length !== 0 &&
                <button
                  className={`tab-button ${activeTab === 'return' ? 'active' : ''}`}
                  onClick={() => setActiveTab('return')}
                >
                  <span className="tab-icon">↩️</span>
                  Return
                </button>
              }

              {orderCreditNoteDetails?.autoCount_status === 'submitted' && orderCreditNoteDetails &&
                <button
                  className={`tab-button ${activeTab === 'credit_note' ? 'active' : ''}`}
                  onClick={() => setActiveTab('credit_note')}
                >
                  <span className="tab-icon">📝</span>
                  Credit Note
                </button>
              }
            </div>
            {activeTab === 'details' && (
              <div className="details-tab">
                {renderOrderDetails()}
              </div>
            )}
            {activeTab === 'tracking' && renderTrackingContentDEMO()}
            {activeTab === 'credit_note' && renderCreditNoteTab()}
            {activeTab === 'messaging' && renderMessagingTab()}
            {activeTab === 'payment_details' && renderPaymentsDetails()}
            {activeTab === 'invoice_details' && renderInvoiceContent()}
            {activeTab === 'return' && renderReturnTab()}
          </LoadingOverlay>
        </div>
      </div>
      <QuotationModal
        isOpen={isQuotationModalOpen}
        toggle={() => setIsQuotationModalOpen(!isQuotationModalOpen)}
        quotationDetails={quotationDetails}
        outOfStockItems={outOfStockItems}
        discount={discount}
        paymentTerms={paymentTerms}
        onPriceChange={handlePriceChange}
        onDiscountChange={handleDiscountChange}
        onPaymentTermsChange={handlePaymentTermsChange}
        onBatchChange={handleBatchChange}
        onQuantityChange={handleQuantityChange}
        onAddBatch={handleAddBatch}
        onRemoveBatch={handleRemoveBatch}
        onSave={saveQuotation}
        onIssue={finalIssueQuotation}
        createNewOrderForOOS={createNewOrderForOOS}
        onToggleCreateNewOrderForOOS={setCreateNewOrderForOOS}
        loading={loading}
      />
      <style>{`
        .app-main__outer {
          background-color: #f8f9fa;
          min-height: 100vh;
          padding-top: 20px;
        }
        .rfq-card {
          border-radius: 10px;
          box-shadow: 0 2px 10px rgba(0,0,0,0.05);
          border: none;
        }
        .form-select, .form-control {
          border-color: #e0e0e0;
          border-radius: 5px;
          padding: 10px;
        }
        .btn-light {
          background-color: #ffffff;
          border-color: #e0e0e0;
          color: #333;
        }
        .btn-primary {
          background-color: #3D607A;
          border-color: #3D607A;
        }
        .text-muted {
          color: #6c757d !important;
        }
        h4, h5 {
          color: #333;
        }
        .item-list {
          border-top: 1px solid #e0e0e0;
          border-bottom: 1px solid #e0e0e0;
          padding: 20px 0;
        }
        .item-image {
          width: 40px;
          height: 40px;
          object-fit: cover;
          border-radius: 5px;
        }
        .totals {
          margin-top: 20px;
        }
        .totals-row {
          display: flex;
          justify-content: flex-end;
          margin-bottom: 5px;
        }
        .total-amount {
          font-weight: bold;
        }
        .header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
        }
        .back-button {
          background: none;
          border: none;
          font-size: 20px;
          cursor: pointer;
        }
        .rfq-id {
          font-weight: bold;
          font-size: 1.5rem;
        }
        .actions {
          display: flex;
          gap: 10px;
        }
        .reject-button,
        .accept-button,
        .edit-button {
          padding: 8px 16px;
          border: none;
          border-radius: 5px;
          cursor: pointer;
          font-weight: bold;
        }
        .reject-button {
          background-color: #ffdddd;
          color: #d9534f;
        }
        .accept-button {
          background-color: #5cb85c;
          color: #fff;
        }
        .approval-button {
          background-color: #d9edf7;
          color:rgb(213, 39, 39);
          border: 1px solid #bce8f1;
        }
        .edit-button {
          background-color: #f0ad4e;
          color: #fff;
        }
        .edit-rfq-button {
          background-color: #3D607A;
          color: #fff;
          border: 1px solid #3D607A;
          padding: 8px 16px;
          border-radius: 5px;
          cursor: pointer;
          font-weight: bold;
          transition: all 0.3s ease;
          display: inline-flex;
          align-items: center;
        }
        .edit-rfq-button:hover {
          background-color: #2d4a5c;
          border-color: #2d4a5c;
        }
        .reject-button, .rejected-button {
          background-color: #ffdddd;
          color: #d9534f;
          border: 1px solid #d9534f;
        }
        .Approved-button {
          background-color: #fcf8e3;
          color:rgb(80, 127, 47);
          border: 1px solid #faebcc;
        }
        .accept-button, .accepted-button {
          background-color: #dff0d8;
          color: #5cb85c;
          border: 1px solid #5cb85c;
        }
        .pay-now-button {
          background-color: #5bc0de;
          color: white;
          border: 1px solid #46b8da;
        }
        .processing-button {
          background-color: #fcf8e3;
          color: #8a6d3b;
          border: 1px solid #faebcc;
        }
        .in-transit-button {
          background-color: #d9edf7;
          color: #31708f;
          border: 1px solid #bce8f1;
        }
        .delivered-button {
          background-color: #dff0d8;
          color: #3c763d;
          border: 1px solid #d6e9c6;
        }
        .pending-button {
          background-color: #f5f5f5;
          color: #777;
          border: 1px solid #ddd;
        }
        button {
          padding: 8px 16px;
          border-radius: 5px;
          cursor: pointer;
          font-weight: bold;
          transition: all 0.3s ease;
        }
        button:hover:not(:disabled) {
          opacity: 0.8;
        }
        button:disabled {
          cursor: not-allowed;
          opacity: 0.6;
        }
        
        /* Enhanced Tab Styling */
        .order-tabs {
          display: flex;
          gap: 8px;
          margin-bottom: 32px;
          padding: 8px;
          background: rgba(255, 255, 255, 0.8);
          border-radius: 16px;
          backdrop-filter: blur(10px);
          box-shadow: 0 4px 20px rgba(61, 96, 122, 0.1);
          border: 1px solid rgba(255, 255, 255, 0.2);
        }
        
        .tab-button {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 20px;
          border: none;
          border-radius: 12px;
          background: transparent;
          color: #64748b;
          font-weight: 600;
          font-size: 14px;
          cursor: pointer;
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
        }
        
        .tab-button:hover {
          background: rgba(61, 96, 122, 0.1);
          color: #3D607A;
          transform: translateY(-1px);
        }
        
        .tab-button.active {
          background: linear-gradient(135deg, #3D607A 0%, #2d4a5c 100%);
          color: #fff;
          box-shadow: 0 4px 16px rgba(61, 96, 122, 0.3);
          transform: translateY(-1px);
        }
        
        .tab-icon {
          font-size: 16px;
          opacity: 0.8;
        }
        
        .tab-button.active .tab-icon {
          opacity: 1;
        }
        
        /* Enhanced Table Styling - Only for Details tab */
        .details-tab table {
          border-collapse: separate;
          border-spacing: 0;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
          background: rgba(255, 255, 255, 0.95);
          width: 100%;
        }
        
        .details-tab table thead th {
          background: linear-gradient(135deg, #3D607A 0%, #2d4a5c 100%);
          color: #fff;
          font-weight: 600;
          padding: 16px 12px;
          border: none;
          font-size: 14px;
          letter-spacing: 0.025em;
          text-align: center;
          position: relative;
        }
        
        .details-tab table thead th:first-child {
          border-top-left-radius: 12px;
        }
        
        .details-tab table thead th:last-child {
          border-top-right-radius: 12px;
        }
        
        .details-tab table tbody tr {
          transition: all 0.3s ease;
          border-bottom: 1px solid rgba(226, 232, 240, 0.5);
          background: rgba(255, 255, 255, 0.9);
        }
        
        .details-tab table tbody tr:hover {
          background: rgba(61, 96, 122, 0.05);
          transform: translateY(-1px);
          box-shadow: 0 2px 8px rgba(61, 96, 122, 0.1);
        }
        
        .details-tab table tbody tr:last-child {
          border-bottom: none;
        }
        
        .details-tab table tbody tr:last-child td:first-child {
          border-bottom-left-radius: 12px;
        }
        
        .details-tab table tbody tr:last-child td:last-child {
          border-bottom-right-radius: 12px;
        }
        
        .details-tab table tbody td {
          padding: 16px 12px;
          border: none;
          font-size: 14px;
          color: #374151;
          vertical-align: middle;
          text-align: center;
        }
        
        /* Item name styling */
        .details-tab table tbody td:first-child {
          font-weight: 600;
          color: #1f2937;
          text-align: center;
        }
        
        /* Quantity styling */
        .details-tab table tbody td:nth-child(2) {
          text-align: center;
          font-weight: 500;
          color: #3D607A;
        }
        
        /* Price styling */
        .details-tab table tbody td:nth-child(3),
        .details-tab table tbody td:nth-child(8) {
          text-align: center;
          font-weight: 600;
          color: #059669;
        }
        
        /* Empty cell styling */
        .details-tab table tbody td {
          color: #6b7280;
        }
        
        .details-tab table tbody td:contains("-") {
          color: #9ca3af;
          font-style: italic;
        }
        
        /* Enhanced Card Styling for item list in details tab */
        .details-tab .item-list {
          background: rgba(248, 250, 252, 0.8);
          border-radius: 16px;
          padding: 24px;
          margin: 24px 0;
          border: 1px solid rgba(226, 232, 240, 0.8);
          box-shadow: 0 4px 20px rgba(61, 96, 122, 0.08);
        }
        
        .chat-container {
          background: linear-gradient(135deg, #3a7bd5, #3a6073);
          border-radius: 12px;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          height: 600px;
          box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
        }
        .messages-container {
          flex-grow: 1;
          overflow-y: auto;
          padding: 15px;
          background-color: #F9F7F7;
        }
        .message-wrapper {
          display: flex;
          margin-bottom: 10px;
        }
        .message-wrapper.sent {
          justify-content: flex-end;
        }
        .message-wrapper.received {
          justify-content: flex-start;
        }
        .message-content {
          max-width: 60%;
          padding: 10px 15px;
          border-radius: 18px;
          position: relative;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
        }
        .message-wrapper.sent .message-content {
          background-color: #00796B;
          color: white;
        }
        .message-wrapper.received .message-content {
          background-color: #1565C0;
          color: white;
        }
        .message-sender {
          font-weight: bold;
          margin-bottom: 4px;
          font-size: 0.9em;
          opacity: 0.8;
        }
        .message-text {
          margin-bottom: 4px;
          line-height: 1.4;
        }
        .message-time {
          font-size: 0.75em;
          opacity: 0.7;
          text-align: right;
        }
        .message-input {
          display: flex;
          align-items: center;
          padding: 15px;
          background-color: rgba(0, 0, 0, 0.2);
        }
        .message-input input {
          flex-grow: 1;
          border: none;
          border-radius: 20px;
          padding: 12px 20px;
          margin-right: 10px;
          background-color: rgba(255, 255, 255, 0.9);
          color: #333;
          font-size: 1em;
        }
        .message-input button {
          background-color: #00796B;
          border: none;
          border-radius: 50%;
          width: 45px;
          height: 45px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: background-color 0.3s ease;
        }
        .message-input button:hover {
          background-color: #00695C;
        }
      `}</style>
    </>
  );
};

export default QuotationsDetails;